import { z } from 'zod';

/**
 * Payments (Module 11 / ADR-005 / ADR-009) — Stripe Connect marketplace
 * split, weekly therapist payouts, monthly invoice export.
 *
 * Anonymity-first applies here too: nothing in this file exposes client
 * identity, and payout/invoice summaries below are deliberately narrower
 * than the therapist-vetting summary in admin.ts — see ADR-009 §10.
 */

// ── Connect onboarding (THERAPIST) ────────────────────────────────────

export const ConnectStatusResponseSchema = z.object({
  connected: z.boolean(), // has a Stripe account at all
  onboarded: z.boolean(), // charges + payouts enabled + details submitted
  chargesEnabled: z.boolean(),
  payoutsEnabled: z.boolean(),
  detailsSubmitted: z.boolean(),
});
export type ConnectStatusResponse = z.infer<typeof ConnectStatusResponseSchema>;

export const OnboardingLinkResponseSchema = z.object({
  url: z.string().url(),
});
export type OnboardingLinkResponse = z.infer<typeof OnboardingLinkResponseSchema>;

// ── Checkout (CLIENT) ──────────────────────────────────────────────────

export const CheckoutResponseSchema = z.object({
  checkoutUrl: z.string().url(),
});
export type CheckoutResponse = z.infer<typeof CheckoutResponseSchema>;

// ── Therapist accept/reject (ADR-010: manual capture) ───────────────────
// The client authorizes payment at booking time; accepting captures the
// held funds, rejecting releases them — zero cost either way until the
// therapist decides.

export const AcceptBookingResponseSchema = z.object({
  success: z.boolean(),
});
export type AcceptBookingResponse = z.infer<typeof AcceptBookingResponseSchema>;

export const RejectBookingRequestSchema = z.object({
  reason: z.string().max(500).optional(),
});
export type RejectBookingRequest = z.infer<typeof RejectBookingRequestSchema>;

export const RejectBookingResponseSchema = z.object({
  success: z.boolean(),
});
export type RejectBookingResponse = z.infer<typeof RejectBookingResponseSchema>;

// ── Payouts ─────────────────────────────────────────────────────────────

export const PayoutStatusSchema = z.enum(['SCHEDULED', 'IN_TRANSIT', 'PAID', 'FAILED']);
export type PayoutStatus = z.infer<typeof PayoutStatusSchema>;

/** No client identity, and no therapist identity either — see ADR-009 §10. Admin cross-references by therapistUserId if needed. */
export const PayoutSummarySchema = z.object({
  id: z.string().cuid(),
  therapistUserId: z.string().cuid(),
  periodStart: z.string().datetime(),
  periodEnd: z.string().datetime(),
  currency: z.string().length(3),
  totalGross: z.string(),
  totalPlatformFee: z.string(),
  totalStripeFee: z.string(),
  totalNet: z.string(),
  status: PayoutStatusSchema,
  stripeTransferId: z.string().nullable(),
  scheduledFor: z.string().datetime(),
  paidAt: z.string().datetime().nullable(),
  failedReason: z.string().nullable(),
  bookingCount: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
});
export type PayoutSummary = z.infer<typeof PayoutSummarySchema>;

export const PayoutListResponseSchema = z.object({
  payouts: z.array(PayoutSummarySchema),
});
export type PayoutListResponse = z.infer<typeof PayoutListResponseSchema>;

export const RetryPayoutResponseSchema = z.object({
  payout: PayoutSummarySchema,
});
export type RetryPayoutResponse = z.infer<typeof RetryPayoutResponseSchema>;

// ── Invoice export (ADMIN) ───────────────────────────────────────────────

export const ExportJobStatusSchema = z.enum(['PENDING', 'RUNNING', 'READY', 'FAILED']);
export type ExportJobStatus = z.infer<typeof ExportJobStatusSchema>;

export const InvoiceExportRequestSchema = z.object({
  year: z.number().int().min(2020).max(2100),
  month: z.number().int().min(1).max(12),
});
export type InvoiceExportRequest = z.infer<typeof InvoiceExportRequestSchema>;

export const InvoiceExportJobSchema = z.object({
  id: z.string().cuid(),
  year: z.number().int(),
  month: z.number().int(),
  status: ExportJobStatusSchema,
  errorMessage: z.string().nullable(),
  invoiceCount: z.number().int().nonnegative(),
  zipAvailable: z.boolean(),
  excelAvailable: z.boolean(),
  createdAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
});
export type InvoiceExportJob = z.infer<typeof InvoiceExportJobSchema>;

export const InvoiceExportJobResponseSchema = z.object({
  job: InvoiceExportJobSchema,
});
export type InvoiceExportJobResponse = z.infer<typeof InvoiceExportJobResponseSchema>;

export const InvoiceExportJobListResponseSchema = z.object({
  jobs: z.array(InvoiceExportJobSchema),
});
export type InvoiceExportJobListResponse = z.infer<typeof InvoiceExportJobListResponseSchema>;
